import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';

const useSeniorityBudgetTierApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 100, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 100 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/seniority-budget-tiers?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 100,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/seniority-budget-tiers', formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/seniority-budget-tiers/${id}`, formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      for (const id of ids) {
        await api.delete(`/v1/seniority-budget-tiers/${id}`);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const seedDefaults = useCallback(async () => {
    setFetching(true);
    try {
      await api.post('/v1/seniority-budget-tiers/seed-defaults');
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('seniorityBudget.name'), options: { filter: false, sort: true } },
    { name: 'seniorityDays', label: t('seniorityBudget.seniorityDays'), options: { filter: false, sort: true } },
    {
      name: 'creditAmount', label: t('seniorityBudget.creditAmount'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => `${Number(v ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`,
      },
    },
    { name: 'recurEveryDays', label: t('seniorityBudget.recurEveryDays'), options: { filter: false, sort: false, customBodyRender: (v) => v ?? '-' } },
    { name: 'applyMultiplierAfterDays', label: t('seniorityBudget.applyMultiplierAfterDays'), options: { filter: false, sort: false, customBodyRender: (v) => v ?? '-' } },
    {
      name: 'multiplierFactor', label: t('seniorityBudget.multiplierFactor'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => v != null ? `${v}x` : '-',
      },
    },
  ], [t]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('seniorityBudget.name'), type: 'text', grid: 12, required: true },
    { name: 'seniorityDays', label: t('seniorityBudget.seniorityDays'), type: 'number', grid: 6, required: true },
    { name: 'creditAmount', label: t('seniorityBudget.creditAmount'), type: 'number', grid: 6, required: true },
    { name: 'recurEveryDays', label: t('seniorityBudget.recurEveryDays'), type: 'number', grid: 6 },
    { name: 'sortOrder', label: t('seniorityBudget.sortOrder'), type: 'number', grid: 6 },
    { name: 'applyMultiplierAfterDays', label: t('seniorityBudget.applyMultiplierAfterDays'), type: 'number', grid: 6 },
    { name: 'multiplierFactor', label: t('seniorityBudget.multiplierFactor'), type: 'number', grid: 6 },
  ], [t]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete, seedDefaults };
};

export default useSeniorityBudgetTierApi;
